import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import type { JobFileEntry } from '@jobos/shared';

import { JobFileThumbnail } from './JobFileThumbnail';

/** How far below and right of the pointer the preview sits. */
export const CURSOR_OFFSET = 14;

// The thing that follows the pointer during a drag.
//
// Deliberately not dnd-kit's DragOverlay. That positions its element at the
// *dragged card's* rect and moves it by the pointer delta, which lines up only
// when the preview is the same size and shape as what was picked up. Ours is a
// small square standing in for a whole card, so it hung off the card's corner —
// further from the pointer the further into a card the grab landed. Correcting
// that with a modifier is worse, not better: modifiers are handed the overlay's
// re-measured rect, which already includes the transform they returned last
// frame, so the correction feeds on itself.
//
// Reading the pointer directly has none of those failure modes. It is also
// immune to the one DragOverlay could not have handled anyway: auto-scrolling
// during a drag moves the overlay's frame of reference out from under it.

interface JobFileDragPreviewProps {
  jobId: string;
  file: JobFileEntry;
  /** How many files are moving; the badge is drawn only past one. */
  count: number;
  /** Where the press began, so the first frame has somewhere to be. */
  origin: { x: number; y: number };
}

export function JobFileDragPreview({ jobId, file, count, origin }: JobFileDragPreviewProps) {
  const [point, setPoint] = useState(origin);

  useEffect(() => {
    // pointermove covers mouse, pen and touch alike. Passive: this only reads
    // the coordinates, and the sensors own whether the gesture is cancelled.
    const track = (event: PointerEvent) => setPoint({ x: event.clientX, y: event.clientY });
    window.addEventListener('pointermove', track, { passive: true });
    return () => window.removeEventListener('pointermove', track);
  }, []);

  return createPortal(
    <div
      data-testid="job-file-drag-preview"
      className="pointer-events-none fixed z-50 h-20 w-20 overflow-hidden rounded-lg border border-gray-300 bg-white shadow-lg"
      style={{ left: point.x + CURSOR_OFFSET, top: point.y + CURSOR_OFFSET }}
    >
      <JobFileThumbnail jobId={jobId} file={file} />
      {count > 1 && (
        <span className="absolute right-0 top-0 flex h-6 min-w-[1.5rem] items-center justify-center rounded-bl-lg rounded-tr-lg bg-blue-600 px-1 text-xs font-semibold text-white">
          {count}
        </span>
      )}
    </div>,
    document.body,
  );
}
